import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { DollarSign, Users, Home, TrendingDown, Info } from 'lucide-react';
import SEO from '../components/SEO';

const LearnMore = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Learn More - Health Insurance Guide"
        description="Understanding health insurance costs, income calculations, household definitions, and ACA subsidies. Simple guide to help you navigate health insurance marketplace and save money."
        keywords="health insurance subsidies, ACA subsidies, MAGI income, household size, health insurance costs, marketplace subsidies, premium tax credits"
        path="/learn-more"
      />
      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-5xl font-bold mb-6">Understanding Health Insurance Costs</h1>
          <p className="text-2xl">A simple guide to income, household, and subsidies</p>
        </div>
      </section>

      {/* Modified Adjusted Gross Income (MAGI) */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <Card className="border-2 border-teal-300 mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <DollarSign className="text-teal-600" size={48} />
                <h2 className="text-4xl font-bold text-gray-900">Modified Adjusted Gross Income (MAGI)</h2>
              </div>
              
              <h3 className="text-2xl font-bold mb-4 text-gray-900">What is MAGI?</h3>
              <p className="text-xl text-gray-800 mb-6 leading-relaxed">
                MAGI stands for "Modified Adjusted Gross Income." It's the number used to determine if you qualify for lower health insurance costs.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-gray-900">How is it calculated?</h3>
              <p className="text-xl text-gray-800 mb-6 leading-relaxed">
                Your MAGI is based on your Adjusted Gross Income (AGI) from your tax return, plus certain additions like:
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-lg text-gray-800">
                <li className="font-semibold">Tax-exempt interest</li>
                <li className="font-semibold">Tax-exempt Social Security benefits</li>
                <li className="font-semibold">Foreign income that isn't taxed</li>
              </ul>

              <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-3 text-teal-900">In Simple Terms:</h3>
                <p className="text-xl text-gray-800 leading-relaxed">
                  Think of MAGI as your total yearly income from all sources. This includes wages, Social Security, pensions, interest, dividends, and other income. It's what the Health Insurance Marketplace uses to see if you qualify for financial help.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Household Size */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <Card className="border-2 border-blue-300 mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <Users className="text-blue-600" size={48} />
                <h2 className="text-4xl font-bold text-gray-900">Household Size</h2>
              </div>
              
              <h3 className="text-2xl font-bold mb-4 text-gray-900">What counts as household size?</h3>
              <p className="text-xl text-gray-800 mb-6 leading-relaxed">
                Your household size includes everyone you claim on your tax return:
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-lg text-gray-800">
                <li className="font-semibold">Yourself</li>
                <li className="font-semibold">Your spouse (if you file jointly)</li>
                <li className="font-semibold">Your dependents (children, relatives you support)</li>
              </ul>

              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 mb-6">
                <h3 className="text-2xl font-bold mb-3 text-blue-900">Examples:</h3>
                <ul className="space-y-3 text-lg text-gray-800">
                  <li>• <strong>Single person:</strong> Household size = 1</li>
                  <li>• <strong>Married couple (no children):</strong> Household size = 2</li>
                  <li>• <strong>Married couple with 2 children:</strong> Household size = 4</li>
                  <li>• <strong>Single parent with 1 child:</strong> Household size = 2</li>
                </ul>
              </div>

              <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-3 text-gray-900">Why does it matter?</h3>
                <p className="text-xl text-gray-800 leading-relaxed">
                  Larger households can have higher income and still qualify for subsidies. The government adjusts income limits based on how many people you're supporting.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Household Income */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <Card className="border-2 border-teal-300 mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <Home className="text-teal-600" size={48} />
                <h2 className="text-4xl font-bold text-gray-900">Household Income</h2>
              </div>
              
              <h3 className="text-2xl font-bold mb-4 text-gray-900">What is household income?</h3>
              <p className="text-xl text-gray-800 mb-6 leading-relaxed">
                Household income is the total income of everyone in your household who is required to file a tax return. This includes:
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-lg text-gray-800">
                <li className="font-semibold">Wages and salaries</li>
                <li className="font-semibold">Social Security benefits</li>
                <li className="font-semibold">Pensions and retirement income</li>
                <li className="font-semibold">Investment income (interest, dividends)</li>
                <li className="font-semibold">Rental income</li>
                <li className="font-semibold">Business income</li>
              </ul>

              <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-3 text-teal-900">Important Note:</h3>
                <p className="text-xl text-gray-800 leading-relaxed">
                  You'll use your estimated income for the year you're getting coverage, not last year's income. If your income changes during the year, you can update your information to adjust your subsidy.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Subsidies */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <Card className="border-2 border-blue-300 mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <TrendingDown className="text-blue-600" size={48} />
                <h2 className="text-4xl font-bold text-gray-900">Subsidies (Premium Tax Credits)</h2>
              </div>
              
              <h3 className="text-2xl font-bold mb-4 text-gray-900">What are subsidies?</h3>
              <p className="text-xl text-gray-800 mb-6 leading-relaxed">
                Subsidies, also called "Premium Tax Credits," are financial help from the government to lower your monthly health insurance premium. If you qualify, the government pays part of your insurance cost directly to the insurance company.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-gray-900">Who qualifies for subsidies?</h3>
              <p className="text-xl text-gray-800 mb-6 leading-relaxed">
                You may qualify if:
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-lg text-gray-800">
                <li className="font-semibold">Your household income is between 100% and 400% of the Federal Poverty Level (FPL)</li>
                <li className="font-semibold">You don't have access to affordable coverage through an employer</li>
                <li className="font-semibold">You're not eligible for Medicare or Medicaid</li>
                <li className="font-semibold">You buy insurance through the Health Insurance Marketplace</li>
              </ul>

              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 mb-6">
                <h3 className="text-2xl font-bold mb-3 text-blue-900">How Subsidies Work:</h3>
                <p className="text-xl text-gray-800 mb-4 leading-relaxed">
                  The subsidy amount is based on your MAGI and household size. The lower your income, the more help you get.
                </p>
                <p className="text-xl text-gray-800 leading-relaxed">
                  <strong>Example:</strong> If your monthly premium is $500 and you qualify for a $300 subsidy, you only pay $200 per month.
                </p>
              </div>

              <div className="bg-green-50 border-2 border-green-400 rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-3 text-green-900">Good News:</h3>
                <p className="text-xl text-gray-800 leading-relaxed">
                  Many people qualify for subsidies and don't realize it. Even if you think you won't qualify, it's worth checking. You might be surprised at how much you can save!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default LearnMore;
